import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';
import CustomSelect from '@shared/components/forms/theme-elements/CustomSelect';
import CustomFormLabel from '@shared/components/forms/theme-elements/CustomFormLabel';
import { StyledErrorText } from '@shared/components/errorText';
import { IconChevronDown } from '@shared/assets/images/svgs';
import KeyValueHeadersEditor from '@features/Admin/PostCallIntegrations/ui/shared/KeyValueHeadersEditor';
import {
  McpToolFormProps,
  McpToolFormData,
  HeaderItem,
  getEmptyMcpToolFormData,
} from './mcp.types';

interface FormErrors {
  mcpName?: string;
  url?: string;
  headers?: string;
  transportType?: string;
}

const McpToolForm: React.FC<McpToolFormProps> = ({
  initialData,
  agents,
  existingTools,
  onSubmit,
  onChange,
  onCancel,
  isLoading,
  disabled,
  fixedAgentId,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [formData, setFormData] = useState<McpToolFormData>(
    initialData || getEmptyMcpToolFormData(),
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const isInitialMount = React.useRef(true);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      const emptyData = getEmptyMcpToolFormData();
      if (fixedAgentId !== undefined) {
        emptyData.agentId = fixedAgentId;
      }
      setFormData(emptyData);
    }
    setErrors({});
    isInitialMount.current = true;
  }, [initialData, fixedAgentId]);

  useEffect(() => {
    if (!isInitialMount.current) {
      onChange?.(formData);
    } else {
      isInitialMount.current = false;
    }
  }, [formData, onChange]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.mcpName.trim()) {
      newErrors.mcpName = t('mcpTools.validation.required');
    } else {
      const trimmedName = formData.mcpName.trim();
      const currentAgentId = formData.agentId || null;

      const duplicateTool = existingTools.find(
        (tool) =>
          tool.mcpName.trim() === trimmedName &&
          (tool.agentId || null) === currentAgentId &&
          tool.id !== initialData?.id,
      );

      if (duplicateTool) {
        newErrors.mcpName = t('mcpTools.validation.uniqueName');
      }
    }

    const url = formData.mcpServerConfig.url.trim();
    if (!url) {
      newErrors.url = t('mcpTools.validation.required');
    } else {
      try {
        new URL(url);
      } catch {
        newErrors.url = t('mcpTools.validation.invalidUrl');
      }
    }

    const headerKeys = formData.mcpServerConfig.headers
      .map((h) => h.key.trim())
      .filter((k) => k !== '');
    const uniqueKeys = new Set(headerKeys);
    if (headerKeys.length !== uniqueKeys.size) {
      newErrors.headers = t('mcpTools.validation.duplicateHeaders');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      await onSubmit(formData);
    }
  };

  const handleFieldChange = (field: string, value: string) => {
    if (field === 'mcpName') {
      setFormData((prev) => ({ ...prev, mcpName: value }));
    } else if (field === 'agentId') {
      setFormData((prev) => ({ ...prev, agentId: value || null }));
    } else if (field === 'url') {
      setFormData((prev) => ({
        ...prev,
        mcpServerConfig: {
          ...prev.mcpServerConfig,
          url: value,
          agentId: formData.agentId || null,
        },
      }));
    } else if (field === 'transportType') {
      setFormData((prev) => ({
        ...prev,
        mcpServerConfig: {
          ...prev.mcpServerConfig,
          transportType: value,
        },
      }));
    }

    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleAddHeader = () => {
    setFormData((prev) => ({
      ...prev,
      mcpServerConfig: {
        ...prev.mcpServerConfig,
        headers: [...prev.mcpServerConfig.headers, { key: '', value: '' }],
      },
    }));
  };

  const handleRemoveHeader = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      mcpServerConfig: {
        ...prev.mcpServerConfig,
        headers: prev.mcpServerConfig.headers.filter((_, i) => i !== index),
      },
    }));
    if (errors.headers) {
      setErrors((prev) => ({ ...prev, headers: undefined }));
    }
  };

  const handleHeaderChange = (index: number, field: keyof HeaderItem, value: string) => {
    setFormData((prev) => ({
      ...prev,
      mcpServerConfig: {
        ...prev.mcpServerConfig,
        headers: prev.mcpServerConfig.headers.map((header, i) =>
          i === index ? { ...header, [field]: value } : header,
        ),
      },
    }));
    if (errors.headers) {
      setErrors((prev) => ({ ...prev, headers: undefined }));
    }
  };

  const isEditing = Boolean(initialData?.id);

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={0}>
        <Box>
          <CustomFormLabel htmlFor="mcp-name" sx={{ mb: 1, fontWeight: 500 }}>
            {t('mcpTools.mcpName')} *
          </CustomFormLabel>
          <CustomTextField
            id="mcp-name"
            placeholder={t('mcpTools.mcpNamePlaceholder')}
            value={formData.mcpName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldChange('mcpName', e.target.value)
            }
            fullWidth
            disabled={disabled || isLoading}
            error={Boolean(errors.mcpName)}
            sx={{
              ...(errors.mcpName && {
                '& fieldset': {
                  borderColor: `${theme.palette.error.main} !important`,
                },
              }),
            }}
          />
          {errors.mcpName && <StyledErrorText>{errors.mcpName}</StyledErrorText>}
        </Box>

        {fixedAgentId === undefined && (
          <Box>
            <CustomFormLabel htmlFor="agent-select" sx={{ mb: 1, fontWeight: 500 }}>
              {t('mcpTools.agent')}{' '}
              <Typography component="span" color="text.secondary" fontSize="12px">
                ({t('common.optional')})
              </Typography>
            </CustomFormLabel>
            <Box sx={{ position: 'relative' }}>
              <CustomSelect
                id="agent-select"
                value={formData.agentId || ''}
                onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                  handleFieldChange('agentId', e.target.value as string)
                }
                fullWidth
                disabled={disabled || isLoading}
                displayEmpty
                IconComponent={IconChevronDown}
                renderValue={(selected: string) => {
                  if (!selected) {
                    return (
                      <Typography color={theme.palette.text.secondary}>
                        {t('mcpTools.selectAgent')}
                      </Typography>
                    );
                  }
                  const agent = agents.find((a) => a.id === selected);
                  return agent?.name || selected;
                }}>
                {agents.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </MenuItem>
                ))}
              </CustomSelect>
              {formData.agentId && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFieldChange('agentId', '');
                  }}
                  disabled={disabled || isLoading}
                  sx={{
                    position: 'absolute',
                    right: '32px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    padding: '4px',
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      color: theme.palette.error.main,
                    },
                  }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {t('mcpTools.agentHelpText')}
            </Typography>
          </Box>
        )}

        <Box>
          <CustomFormLabel htmlFor="mcp-url" sx={{ mb: 1, fontWeight: 500 }}>
            {t('mcpTools.serverUrl')} *
          </CustomFormLabel>
          <CustomTextField
            id="mcp-url"
            placeholder={t('mcpTools.serverUrlPlaceholder')}
            value={formData.mcpServerConfig.url}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFieldChange('url', e.target.value)
            }
            fullWidth
            disabled={disabled || isLoading}
            error={Boolean(errors.url)}
            sx={{
              ...(errors.url && {
                '& fieldset': {
                  borderColor: `${theme.palette.error.main} !important`,
                },
              }),
            }}
          />
          {errors.url && <StyledErrorText>{errors.url}</StyledErrorText>}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {t('mcpTools.urlHelpText')}
          </Typography>
        </Box>

        <Box>
          <CustomFormLabel htmlFor="transport-type" sx={{ mb: 1, fontWeight: 500 }}>
            {t('mcpTools.transportType')} *
          </CustomFormLabel>
          <CustomSelect
            id="transport-type"
            value={formData.mcpServerConfig.transportType}
            onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
              handleFieldChange('transportType', e.target.value as string)
            }
            fullWidth
            disabled={disabled || isLoading}
            IconComponent={IconChevronDown}
            sx={{
              ...(errors.transportType && {
                '& fieldset': {
                  borderColor: `${theme.palette.error.main} !important`,
                },
              }),
            }}>
            <MenuItem value="sse">{t('mcpTools.transportTypeSSE')}</MenuItem>
            <MenuItem value="streamable-http">{t('mcpTools.transportTypeStreamableHTTP')}</MenuItem>
          </CustomSelect>
          {errors.transportType && <StyledErrorText>{errors.transportType}</StyledErrorText>}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {t('mcpTools.transportTypeHelp')}
          </Typography>
        </Box>

        <KeyValueHeadersEditor
          label={`${t('mcpTools.headers')} (${t('common.optional')})`}
          addLabel={t('mcpTools.addHeader')}
          keyLabel={t('mcpTools.headerKey')}
          valueLabel={t('mcpTools.headerValue')}
          emptyLabel={t('mcpTools.noHeaders')}
          keyPlaceholder={t('mcpTools.keyPlaceholder')}
          valuePlaceholder={t('mcpTools.valuePlaceholder')}
          items={formData.mcpServerConfig.headers}
          error={errors.headers}
          disabled={disabled || isLoading}
          onAdd={handleAddHeader}
          onRemove={handleRemoveHeader}
          onChange={handleHeaderChange}
        />

        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={onCancel} disabled={isLoading} sx={{ minWidth: 100 }}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={disabled || isLoading}
            sx={{ minWidth: 140 }}>
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : isEditing ? (
              t('mcpTools.updateTool')
            ) : (
              t('mcpTools.saveTool')
            )}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default McpToolForm;
